import { apiClient } from '@/app/api/client';

export interface UserDto {
  id: string;
  email: string;
  display_name: string | null;
  created_at: string;
  last_login_at: string | null;
  is_system_admin: boolean;
  firebaseUid?: string;
  settings?: Record<string, any>;
}

export interface CreateUserRequest {
  email: string;
  password: string;  // Required for Firebase user creation
  display_name?: string;
  is_system_admin?: boolean;
}

export interface UpdateUserRequest {
  display_name?: string;
  is_system_admin?: boolean;
  password?: string;  // Optional for updating Firebase user password
}

export const usersApi = {
  /**
   * Get all users
   */
  async getAllUsers(): Promise<UserDto[]> {
    const { data } = await apiClient.get('/api/admin/users');
    return data;
  },

  /**
   * Get user by ID
   */
  async getUserById(id: string): Promise<UserDto> {
    const { data } = await apiClient.get(`/api/admin/users/${id}`);
    return data;
  },

  /**
   * Create a new user
   * This will create the user in Firebase first, then in our database
   */
  async createUser(user: CreateUserRequest): Promise<UserDto> {
    // Email and password are required for Firebase user creation
    if (!user.email || !user.password) {
      throw new Error('Email and password are required for user creation');
    }
    
    const { data } = await apiClient.post('/api/admin/users', user);
    return data;
  },

  /**
   * Update an existing user
   * This will update the user in our database and in Firebase if possible
   */
  async updateUser(id: string, user: UpdateUserRequest): Promise<UserDto> {
    const { data } = await apiClient.put(`/api/admin/users/${id}`, user);
    return data;
  },

  /**
   * Delete a user
   * This will delete the user from Firebase first, then from our database
   */
  async deleteUser(id: string): Promise<void> {
    await apiClient.delete(`/api/admin/users/${id}`);
  },

  /**
   * Get the current user
   */
  async getCurrentUser(): Promise<UserDto> {
    const { data } = await apiClient.get('/api/admin/users/me');
    return data;
  },

  /**
   * Set admin status for a user
   */
  async setAdminStatus(id: string, isAdmin: boolean): Promise<UserDto> {
    const { data } = await apiClient.put(`/api/admin/users/${id}/admin?isAdmin=${isAdmin}`);
    return data;
  },

  /**
   * Get manageable Durable Objects for a user
   */
  async getManageableDos(userId: string): Promise<{ name: string; description: string }[]> {
    const { data } = await apiClient.get(`/admin/users/${userId}/dos`);
    return data;
  },

  /**
   * Get the state of a Durable Object for a user
   */
  async getDoState(userId: string, doName: string): Promise<any> {
    const { data } = await apiClient.get(`/admin/users/${userId}/dos/${doName}`);
    return data;
  },

  /**
   * Reset a Durable Object for a user
   */
  async resetDo(userId: string, doName: string): Promise<void> {
    await apiClient.post(`/admin/users/${userId}/dos/${doName}/reset`);
  },

  /**
   * Set user type for a user
   */
  async setUserType(id: string, userType: 'personal' | 'business'): Promise<UserDto> {
    const { data } = await apiClient.put(`/api/admin/users/${id}/type`, { userType });
    return data;
  },

  /**
   * Update a user's settings
   */
  async updateUserSettings(id: string, settings: Record<string, any>): Promise<UserDto> {
    const { data } = await apiClient.put(`/api/admin/users/${id}/settings`, { settings });
    return data;
  }
};

/**
 * Hook for users service
 */
export const useUsersService = () => {
  const usersApi = {
    getAllUsers: async (): Promise<UserDto[]> => {
      const { data } = await apiClient.get('/api/admin/users');
      return data;
    },
    getUserById: async (id: string): Promise<UserDto> => {
      const { data } = await apiClient.get(`/api/admin/users/${id}`);
      return data;
    },
    createUser: async (user: CreateUserRequest): Promise<UserDto> => {
      const { data } = await apiClient.post('/api/admin/users', user);
      return data;
    },
    updateUser: async (id: string, user: UpdateUserRequest): Promise<UserDto> => {
      const { data } = await apiClient.put(`/api/admin/users/${id}`, user);
      return data;
    },
    deleteUser: async (id: string): Promise<void> => {
      await apiClient.delete(`/api/admin/users/${id}`);
    },
    getCurrentUser: async (): Promise<UserDto> => {
      const { data } = await apiClient.get('/api/admin/users/me');
      return data;
    },
    setAdminStatus: async (id: string, isAdmin: boolean): Promise<UserDto> => {
      const { data } = await apiClient.put(`/api/admin/users/${id}/admin`, { isAdmin });
      return data;
    },
    setUserType: async (id: string, userType: 'personal' | 'business'): Promise<UserDto> => {
      const { data } = await apiClient.put(`/api/admin/users/${id}/type`, { userType });
      return data;
    },
    getManageableDos: async (userId: string): Promise<{ name: string; description: string }[]> => {
      const { data } = await apiClient.get(`/api/admin/users/${userId}/dos`);
      return data;
    },
    getDoState: async (userId: string, doName: string): Promise<any> => {
      const { data } = await apiClient.get(`/api/admin/users/${userId}/dos/${doName}`);
      return data;
    },
    resetDo: async (userId: string, doName: string): Promise<void> => {
      await apiClient.post(`/api/admin/users/${userId}/dos/${doName}/reset`);
    },
    updateUserSettings: async (id: string, settings: Record<string, any>): Promise<UserDto> => {
      const { data } = await apiClient.put(`/api/admin/users/${id}/settings`, { settings });
      return data;
    },
  };

  return usersApi;
};
